// src/components/no-connection-screen/no-connection-screen.tsx
import React from 'react';
import { useNetwork } from '../../contexts/network-context';

/**
 * Full-screen overlay shown when the device has no internet connection.
 * Uses inline styles (no Emotion) so it renders even if stylesheets fail to load.
 * Matches the visual language of UpdateRequiredScreen and ErrorBoundary.
 */
export const NoConnectionScreen: React.FC = () => {
  const { connected, checkNow } = useNetwork();
  const [checking, setChecking] = React.useState(false);

  if (connected) return null;

  const handleRetry = async () => {
    setChecking(true);
    await checkNow();
    // Small delay so the user sees the spinner even if check is instant
    setTimeout(() => setChecking(false), 600);
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label="No internet connection"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        display: 'grid',
        placeItems: 'center',
        background: '#000000',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
    >
      {/* Radial glow (matches UpdateRequiredScreen) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(800px 500px at 50% 45%, rgba(255,255,255,0.08), transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 20,
          padding: 24,
          maxWidth: 320,
          textAlign: 'center',
        }}
      >
        {/* Wi-Fi off icon (inline SVG) */}
        <svg
          width="56"
          height="56"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{
            filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.45))',
          }}
        >
          {/* Wi-Fi arcs */}
          <path d="M1 9l2 2a12.73 12.73 0 0 1 6.42-3.17" />
          <path d="M14.57 7.83A12.73 12.73 0 0 1 21 9l2 2" />
          <path d="M5 13l2 2a7.59 7.59 0 0 1 3.35-1.86" />
          <path d="M13.64 13.14A7.59 7.59 0 0 1 17 13l2 2" />
          {/* Dot */}
          <circle cx="12" cy="19" r="1" fill="rgba(255,255,255,0.7)" stroke="none" />
          {/* Slash */}
          <line x1="2" y1="2" x2="22" y2="22" stroke="#E15B5B" strokeWidth="2" />
        </svg>

        <p
          style={{
            color: '#ffffff',
            fontSize: 18,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 600,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          No Internet Connection
        </p>

        <p
          style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: 14,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 400,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          Please check your connection and try again.
        </p>

        <button
          onClick={handleRetry}
          disabled={checking}
          style={{
            marginTop: 8,
            padding: '12px 32px',
            background: checking ? '#3d5bb3' : '#5E84ED',
            color: '#ffffff',
            border: 'none',
            borderRadius: 10,
            fontSize: 15,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 600,
            cursor: checking ? 'default' : 'pointer',
            transition: 'opacity 150ms ease, background 150ms ease',
            opacity: checking ? 0.7 : 1,
          }}
        >
          {checking ? 'Checking...' : 'Retry'}
        </button>
      </div>
    </div>
  );
};
